import { Box } from '@mui/material';
import { useLocation } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../auth';
import {
  getAdminNavigationItems,
  getBottomNavigationItems,
  getNavigationItems,
} from './items';
import type {
  BottomNavigationItemsProps,
  NavigationItem,
  NavigationProps,
} from './types';
import { navContainerSx, navGroupSx } from './styles';
import { filterItemsByPermission, isPathActive } from './utils';
import { NavItem } from './NavItem';

export const Navigation: React.FC<NavigationProps> = ({
  isCollapsed = false,
  showOnlyBottom = false,
  onNavigationClick,
  isMobileDialog = false,
}) => {
  const { t } = useTranslation(['common']);
  const location = useLocation();
  const { user } = useAuthContext();

  const navigationItems = getNavigationItems(t);
  const adminItems = getAdminNavigationItems(t);

  const filteredItems = filterItemsByPermission(navigationItems, user);
  const filteredAdminItems = filterItemsByPermission(adminItems, user);

  // Combine all items
  const allItems = [...filteredItems, ...filteredAdminItems];

  // Render navigation item
  const renderNavItem = (item: NavigationItem) => {
    const isActive = isPathActive(location.pathname, item.to);
    // Disable desktop-only items inside the mobile dialog
    const isDisabled = isMobileDialog && item.isDesktopOnly === true;

    return (
      <NavItem
        key={item.to}
        item={item}
        isCollapsed={isCollapsed}
        isActive={isActive}
        isMobileDialog={isMobileDialog}
        isDisabled={isDisabled}
        onNavigationClick={onNavigationClick}
      />
    );
  };

  if (showOnlyBottom) {
    return <Box sx={navGroupSx}>{allItems.map(renderNavItem)}</Box>;
  }

  return (
    <Box component="nav" sx={navContainerSx}>
      {allItems.map(renderNavItem)}
    </Box>
  );
};

// Bottom navigation items component for bottom placement
export const BottomNavigationItems: React.FC<BottomNavigationItemsProps> = ({
  isCollapsed = false,
  onNavigationClick,
  isMobileDialog = false,
}) => {
  const { t } = useTranslation(['common']);
  const location = useLocation();
  const { user } = useAuthContext();

  const bottomNavigationItems = getBottomNavigationItems();

  // Filter items based on user permissions
  const filteredItems = filterItemsByPermission(bottomNavigationItems, user);

  // Render navigation item
  const renderNavItem = (item: NavigationItem) => {
    const isActive = isPathActive(location.pathname, item.to);
    return (
        <NavItem
          key={item.to}
          item={item}
          isCollapsed={isCollapsed}
          isActive={isActive}
          isMobileDialog={isMobileDialog}
          isDisabled={false}
          onNavigationClick={onNavigationClick}
        />
    );
  };

  // Don't render anything if no items are available for the user
  if (filteredItems.length === 0) {
    return null;
  }

  return <Box sx={navGroupSx} className={'filteredItems'}>{filteredItems.map(renderNavItem)}</Box>;
};
